import { ActionContext, ActionTree } from "vuex";
import { IFDGState } from "@/core/interfaces/state/fdgState.interface";
import FDGService from "@/modules/FDG/services/fdg.service";
import { IFDG } from "@/core/interfaces/state/fdg.interface";
import { IFDGDetail } from "@/core/interfaces/state/fdgDetail.interface";
import { FDGBookingDetailRequestModel } from "@/modules/FDG/models/fdgBookingDetail.request";
import {
  IFDGBookingDetail,
  IFDGBookingPassenger,
} from "@/core/interfaces/state/fdgBookingDetail.interface";
import { FDGBookingConfirmRequestModel } from "@/modules/FDG/models/fdgBookingConfirm.request";
import { FDGBookingConfirmWithoutTravelerDetailsRequestModel } from "@/modules/FDG/models/fdgBookingConfirmWithoutTravelerDetails.request";
import { TOUR_TYPES } from "@/core/enums/TOUR_TYPE";
import { StoreState } from "@/store/type";
import { FDGUpdatePassengerRequest } from "../models/fdgUpdatePassenger.request";
import {
  FDGCancelRequest,
  FDGPayNowRequest,
} from "../models/fdgPaynow.request";

const actions: ActionTree<IFDGState, StoreState> = {
  async fetchGroupFlights(
    context: ActionContext<IFDGState, StoreState>,
    tourType: TOUR_TYPES,
  ) {
    context.commit("enableIsFetchingGroupFlights", true);
    const fdgService = new FDGService();
    const response: IFDG[] = await fdgService.fetchGroupFlights(tourType);
    context.commit("saveGroupFlights", response);
  },

  async fetchGroupFlightDetail(
    context: ActionContext<IFDGState, StoreState>,
    id: string,
  ) {
    context.commit("enableIsFetchingGroupFlightDetail", true);
    const fdgService = new FDGService();
    const response: IFDGDetail | null =
      await fdgService.fetchGroupFlightDetail(id);
    context.commit("saveGroupFlightDetail", response);
  },

  async initiateGroupFlightBooking(
    context: ActionContext<IFDGState, StoreState>,
    {
      payload,
      callback,
    }: {
      payload: FDGBookingDetailRequestModel;
      callback: (bookingId: string) => void;
    },
  ) {
    context.commit("enableIsInitiatingGroupFlightBooking", true);
    const fdgService = new FDGService();
    await fdgService.initiateBooking(payload, callback);
    context.commit("disableIsInitiatingGroupFlightBooking", false);
  },

  async confirmGroupFlightBooking(
    context: ActionContext<IFDGState, StoreState>,
    {
      payload,
      callback,
    }: {
      payload: FDGBookingConfirmRequestModel;
      callback: () => void;
    },
  ) {
    context.commit("enableIsConfirmGroupFlightBooking");
    const fdgService = new FDGService();
    await fdgService.confirmBooking(payload, callback);
    context.commit("disableIsConfirmGroupFlightBooking");
  },

  async confirmGroupFlightBookingWithoutTravelerDetails(
    context: ActionContext<IFDGState, StoreState>,
    {
      payload,
      callback,
    }: {
      payload: FDGBookingConfirmWithoutTravelerDetailsRequestModel;
      callback: () => void;
    },
  ) {
    context.commit("enableIsConfirmGroupFlightBookingWithoutTravelerDetails");
    const fdgService = new FDGService();
    await fdgService.confirmGroupFlightBookingWithoutTravelerDetails(
      payload,
      callback,
    );
    context.commit("disableIsConfirmGroupFlightBookingWithoutTravelerDetails");
  },

  async fetchGroupFlightBookingDetail(
    context: ActionContext<IFDGState, StoreState>,
    bookingId: string,
  ) {
    context.commit("enableIsFetchingGroupFlightBookingDetail", true);
    const fdgService = new FDGService();
    const response: IFDGBookingDetail | null =
      await fdgService.fetchGroupFlightBookingDetail(bookingId);
    context.commit("saveGroupFlightBookingDetail", response);
  },

  async generateGroupFlightBookingOTP(
    context: ActionContext<IFDGState, StoreState>,
    {
      booking_id,
      callback,
    }: {
      booking_id: string;
      callback: () => void;
    },
  ) {
    context.commit("setOTPVerificationInProgress", true);
    const fdgService = new FDGService();
    const otpReferenceId: string =
      await fdgService.generateGroupFlightBookingOTP(
        booking_id,
        callback,
        context,
      );
    context.commit("saveGroupBookingFormOTPReference", otpReferenceId);
    context.commit("setOTPVerificationInProgress", false);
  },

  async getGroupBookingPassengers(
    context: ActionContext<IFDGState, StoreState>,
    booking_id: string,
  ) {
    context.commit("setGroupBookingPassengersFetching", true);
    const fdgService = new FDGService();
    const response: IFDGBookingPassenger[] =
      await fdgService.getGroupBookingPassengers(booking_id);
    context.commit("setGroupBookingPassengersFetching", false);
    context.commit("saveGroupBookingPassengers", response);
  },
  async generateGroupFlightCancelOTP(
    context: ActionContext<IFDGState, StoreState>,
    {
      booking_id,
      callback,
    }: {
      booking_id: string;
      callback: () => void;
    },
  ) {
    context.commit("setOTPVerificationInProgress", true);
    const fdgService = new FDGService();
    const otpReferenceId: string =
      await fdgService.generateGroupFlightCancelOTP(
        booking_id,
        callback,
        context,
      );
    context.commit("saveGroupBookingFormOTPReference", otpReferenceId);
    context.commit("setOTPVerificationInProgress", false);
  },
  async updateGroupBookingPassengers(
    context: ActionContext<IFDGState, StoreState>,
    {
      booking_id,
      payload,
      callback,
    }: {
      booking_id: string;
      payload: FDGUpdatePassengerRequest;
      callback: () => void;
    },
  ) {
    context.commit("setGroupBookingPassengersFetching", true);
    const fdgService = new FDGService();
    await fdgService.updateGroupBookingPassengers(booking_id, payload);
    callback();
    context.commit("setGroupBookingPassengersFetching", false);
  },

  async groupFlightsPaynow(
    context: ActionContext<IFDGState, StoreState>,
    {
      payload,
      callback,
    }: {
      payload: FDGPayNowRequest;
      callback: () => void;
    },
  ) {
    context.commit("setGroupFlightsPaynowLoading", true);
    const fdgService = new FDGService();
    await fdgService.groupFlightsPayNow(payload, callback);
    context.commit("setGroupFlightsPaynowLoading", false);
  },
  async groupFlightsCancel(
    context: ActionContext<IFDGState, StoreState>,
    {
      payload,
      callback,
    }: {
      payload: FDGCancelRequest;
      callback: () => void;
    },
  ) {
    context.commit("setGroupFlightsCancelLoading", true);
    const fdgService = new FDGService();
    await fdgService.groupFlightsCancel(payload, callback);
    context.commit("setGroupFlightsCancelLoading", false);
  },
};

export default actions;
